import React, { useState, useRef, useEffect } from "react";
import { 
  X, 
  Video, 
  Camera, 
  Sparkles, 
  Copy, 
  Check, 
  Layers, 
  Eye, 
  Film,
  SunMedium,
  RotateCw,
  Maximize2,
  Minimize2,
  Box,
  Cpu,
  Download,
  Terminal,
  Zap,
  ShieldCheck,
  Building2,
  Sun,
  Droplets,
  Wrench,
  Wind
} from "lucide-react";
import { toast } from "sonner";

interface PrototypeCinematicStudioProps {
  isOpen: boolean;
  onClose: () => void;
  projectTitle: string;
  projectDescription?: string;
  ecosystem?: "aura-os" | "prometheus-lab";
  discipline?: string;
  bom?: any[];
}

interface Point3D {
  x: number;
  y: number;
  z: number;
}

interface Face3D {
  indices: number[];
  color?: string;
  partName?: string;
  explodeOffset?: Point3D;
}

interface Mesh3D {
  name: string;
  vertices: Point3D[];
  faces: Face3D[];
  dims: { widthMm: number; heightMm: number; depthMm: number };
  specBadge: string;
  metrics: { weightG: number; material: string; printHours?: number; prStandard?: string };
}

export const PrototypeCinematicStudio: React.FC<PrototypeCinematicStudioProps> = ({
  isOpen,
  onClose,
  projectTitle,
  projectDescription,
  ecosystem = "prometheus-lab",
  discipline
}) => {
  const [activeTab, setActiveTab] = useState<"finishedRender" | "viewport3d" | "cinematicPrompt">("finishedRender");
  const [isPhotoZoomed, setIsPhotoZoomed] = useState<boolean>(false);
  const [viewWireframe, setViewWireframe] = useState<boolean>(false);
  const [autoRotate, setAutoRotate] = useState<boolean>(true);
  const [explodedView, setExplodedView] = useState<boolean>(false);
  const [materialFinish, setMaterialFinish] = useState<"petg" | "concrete" | "aluminum" | "hologram">("petg");
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const [cameraRotX, setCameraRotX] = useState<number>(0.35);
  const [cameraRotY, setCameraRotY] = useState<number>(0.65);
  const [isGeneratingModel, setIsGeneratingModel] = useState<boolean>(false);

  // Cinematic prompt controls
  const [cameraMotion, setCameraMotion] = useState<"orbit360" | "macroHero" | "explosiveAssembly" | "fpvDrone">("orbit360");
  const [lightingStyle, setLightingStyle] = useState<"cyberNeon" | "industrialStudio" | "sunsetTropical" | "cleanMinimal">("cyberNeon");
  const [renderQuality, setRenderQuality] = useState<"8kPhotoreal" | "cinematicFilm" | "blueprintHologram">("8kPhotoreal");
  const [copiedPrompt, setCopiedPrompt] = useState<boolean>(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDraggingRef = useRef<boolean>(false);
  const lastMousePos = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const isAura = ecosystem === "aura-os";

  // Identify model archetype based on project title
  const modelType = React.useMemo(() => {
    const t = (projectTitle || "").toLowerCase();
    if (t.includes("coffee") || t.includes("concreto") || t.includes("comercial") || t.includes("arquitectur")) return "coffeeshop";
    if (t.includes("solar") || t.includes("lifepo4") || t.includes("batería") || t.includes("luma")) return "solar";
    if (t.includes("cisterna") || t.includes("agua") || t.includes("bomba")) return "cistern";
    if (t.includes("taller") || t.includes("maker") || t.includes("laboratorio") || t.includes("físico")) return "workshop";
    if (t.includes("gabinete") || t.includes("ip67") || t.includes("carcasa") || t.includes("petg")) return "enclosure";
    if (t.includes("eólica") || t.includes("turbina") || t.includes("savonius") || t.includes("viento")) return "wind";
    return "generic";
  }, [projectTitle]);

  // Map project to its photorealistic finished render image
  const finishedRenderImage = React.useMemo(() => {
    if (modelType === "coffeeshop") return "/renders/coffeeshop_finished.jpg";
    if (modelType === "solar") return "/renders/solar_station_finished.jpg";
    if (modelType === "cistern") return "/renders/cistern_finished.jpg";
    if (modelType === "workshop") return "/renders/workshop_finished.jpg";
    if (modelType === "enclosure") return "/renders/enclosure_finished.jpg";
    if (modelType === "wind") return "/renders/savonius_finished.jpg";
    return "/renders/coffeeshop_finished.jpg";
  }, [modelType]);

  // Finished project technical handover details
  const finishedProjectDetails = React.useMemo(() => {
    switch (modelType) {
      case "coffeeshop":
        return {
          subtitle: "Local Gastronómico Comercial en Concreto Armado y Acero (ASCE 7)",
          features: [
            "Muros perimetrales de hormigón repellado liso resistente a vientos de 175+ MPH (ASCE 7-22 Categoría 5)",
            "Ventanales comerciales templados con marcos de aluminio anodizado negro mate",
            "Rampa de acceso accesible ADA obligatoria en Puerto Rico con pasamanos de acero inoxidable",
            "Barra barista en madera maciza tratada con acometida eléctrica dedicada de 240V para máquina de espresso"
          ],
          asBuiltSpecs: [
            { label: "Área Construida", val: "720 sq ft (24x30 ft)" },
            { label: "Resistencia Viento", val: "ASCE 7-22 · 175+ MPH" },
            { label: "Acometida Eléctrica", val: "120V / 240V Monofásica 200A" },
            { label: "Permisos PR", val: "OGPe / Salud Ambiental / ADA" }
          ]
        };
      case "solar":
        return {
          subtitle: "Sistema Solar Híbrido Crítico 48V con Baterías LiFePO4 (Respaldo LUMA)",
          features: [
            "Inversor híbrido 48V 5000W montado en pared con canaletas metálicas EMT curvadas a 90°",
            "Rack industrial con 5 módulos de batería LiFePO4 de 48V 100Ah (25.6 kWh total de reserva)",
            "Fusible Clase T de 150A para protección de arco y switch de desconexión rápida DC",
            "Subpanel de cargas críticas aislado de la red comercial de LUMA Energy con transferencia instantánea (<15ms)"
          ],
          asBuiltSpecs: [
            { label: "Potencia Inversor", val: "5,000 W Continuos (Onda Pura)" },
            { label: "Capacidad Batería", val: "25.6 kWh (LiFePO4 Grado A)" },
            { label: "Tiempo de Autonomía", val: "36-48 Horas Cargas Críticas" },
            { label: "Normativa Eléctrica", val: "NEC 706 / LUMA Compliant" }
          ]
        };
      case "cistern":
        return {
          subtitle: "Sistema Hidroneumático de Agua Potable 600G con Telemetría IoT",
          features: [
            "Tanque cilíndrico de polietileno virgen virgen de 600 galones certificado NSF-61 para agua potable",
            "Losa de cimentación de concreto armado de 4 pulgadas autonivelada sobre terreno firme",
            "Bomba centrífuga periférica de 3/4 HP con press-control digital electrónico de presión constante (45 PSI)",
            "Sensor ultrasónico impermeable en la tapa con telemetría en vivo vía Wi-Fi / LoRa para monitoreo en celular"
          ],
          asBuiltSpecs: [
            { label: "Capacidad de Reserva", val: "600 Galones (~2,270 L)" },
            { label: "Presión de Servicio", val: "45 PSI Constante" },
            { label: "Autonomía de Agua", val: "10-14 Días Uso Familiar" },
            { label: "Tuberías & Válvulas", val: "PVC Cédula 40 + Check Bronce" }
          ]
        };
      case "workshop":
        return {
          subtitle: "Estación de Trabajo Maker & Laboratorio I+D Físico Prometheus",
          features: [
            "Banco de trabajo pesado en madera de pino tratada antipolilla con estructura de acero estructural tubular",
            "Cerramiento acústico y térmico con impresora 3D Bambu Lab X1-Carbon integrada con sistema AMS multireel",
            "Brazo articulado de extracción forzada de humos de soldadura y partículas de impresión hacia el exterior",
            "Mesa electroestática con tapete disipativo ESD, osciloscopio digital, estación de soldar y panel portaherramientas"
          ],
          asBuiltSpecs: [
            { label: "Superficie de Trabajo", val: "72 x 36 Pulgadas (180x90 cm)" },
            { label: "Capacidad de Carga", val: "1,000 lbs (450 kg)" },
            { label: "Protección Ambiental", val: "Control Salitre y Humedad PR" },
            { label: "Ventilación", val: "Extracción Activa 250 CFM" }
          ]
        };
      case "enclosure":
        return {
          subtitle: "Carcasa Estanca IP67 Impresa en Bambu Lab PETG",
          features: [
            "Cuerpo y tapa con ranura mecanizada para junta continua de neopreno hermética contra inmersión y lluvia",
            "Cuatro tornillos hexagonales de acero inoxidable 316 sobre insertos roscados de latón fijados térmicamente",
            "Prensaestopas de latón PG7 con alivio de tensión para cableado exterior resistente al salitre",
            "Ventana frontal sellada con silicona RTV para pantalla OLED monocromática de estado"
          ],
          asBuiltSpecs: [
            { label: "Grado de Protección", val: "IP67 (Sumergible 1 metro)" },
            { label: "Material Filamento", val: "Bambu Lab PETG Negro Mate" },
            { label: "Tornillería", val: "M3 x 16mm Acero Inox 316" },
            { label: "Resistencia UV", val: "ASTM B117 (Salitre Costero)" }
          ]
        };
      case "wind":
        return {
          subtitle: "Microgenerador Eólico Savonius para Brisa Marina Costera",
          features: [
            "Álabes helicoidales curvados aerodinámicamente en PETG blanco y cian con estabilizador UV",
            "Mástil vertical y bridas de anclaje fabricados en acero inoxidable 316 resistente al salitre marino",
            "Rodamientos cerámicos sellados de ultra baja fricción para autoarranque con brisa leve (3.5 m/s)",
            "Alternador de imanes permanentes (PMG) con rectificador trifásico para recarga nocturna de baterías 12V/24V"
          ],
          asBuiltSpecs: [
            { label: "Velocidad de Arranque", val: "3.5 m/s (~7.8 mph)" },
            { label: "Potencia Nominal", val: "150W - 300W a 10 m/s" },
            { label: "Altura Total", val: "1.20 metros" },
            { label: "Ambiente", val: "Litoral Marino de Puerto Rico" }
          ]
        };
      default:
        return {
          subtitle: "Prototipo de Ingeniería Avanzada de Alta Fidelidad",
          features: [
            "Geometría volumétrica final optimizada para manufactura",
            "Selección de materiales adaptados al clima tropical de Puerto Rico",
            "Criterios de validación y tolerancias verificadas"
          ],
          asBuiltSpecs: [
            { label: "Estado", val: "Fase de Producción Final" },
            { label: "Normativa", val: "Estándar de Taller Prometheus" }
          ]
        };
    }
  }, [modelType]);

  // Construct 3D Mesh
  const meshData = React.useMemo<Mesh3D>(() => {
    const vertices: Point3D[] = [];
    const faces: Face3D[] = [];

    const addBox = (
      x: number, y: number, z: number,
      w: number, h: number, d: number,
      color?: string,
      partName?: string,
      explodeOffset?: Point3D
    ) => {
      const start = vertices.length;
      const hw = w / 2;
      const hh = h / 2;
      const hd = d / 2;

      vertices.push(
        { x: x - hw, y: y - hh, z: z - hd },
        { x: x + hw, y: y - hh, z: z - hd },
        { x: x + hw, y: y + hh, z: z - hd },
        { x: x - hw, y: y + hh, z: z - hd },
        { x: x - hw, y: y - hh, z: z + hd },
        { x: x + hw, y: y - hh, z: z + hd },
        { x: x + hw, y: y + hh, z: z + hd },
        { x: x - hw, y: y + hh, z: z + hd }
      );

      // 6 quads (12 triangles)
      const quads = [
        [0, 1, 2, 3], // Front
        [5, 4, 7, 6], // Back
        [4, 0, 3, 7], // Left
        [1, 5, 6, 2], // Right
        [3, 2, 6, 7], // Top
        [4, 5, 1, 0]  // Bottom
      ];

      quads.forEach((q) => {
        faces.push({ indices: [start + q[0], start + q[1], start + q[2]], color, partName, explodeOffset });
        faces.push({ indices: [start + q[0], start + q[2], start + q[3]], color, partName, explodeOffset });
      });
    };

    if (modelType === "coffeeshop") {
      // Concrete Foundation Slab
      addBox(0, 70, 0, 240, 15, 180, "#475569", "Losa de Cimentación de Concreto 3000 PSI", { x: 0, y: 30, z: 0 });
      // Left concrete block wall
      addBox(-110, 10, 0, 18, 105, 180, "#64748b", "Muros Bloque 8 Pulgadas Rellenos", { x: -40, y: 0, z: 0 });
      // Right concrete block wall
      addBox(110, 10, 0, 18, 105, 180, "#64748b", "Muro Lateral Este (Huracán ASCE 7)", { x: 40, y: 0, z: 0 });
      // Back wall
      addBox(0, 10, -85, 202, 105, 18, "#475569", "Muro Posterior Estructural", { x: 0, y: 0, z: -40 });
      // Sloped Roof Slab
      addBox(0, -52, 0, 256, 14, 196, "#334155", "Losa Techo Inclinada 175+ MPH", { x: 0, y: -60, z: 0 });
      // Commercial Front Counter
      addBox(0, 45, 10, 120, 36, 40, "#d97706", "Mostrador Barista & Atención", { x: 0, y: 0, z: 25 });
      // ADA Entrance Ramp
      addBox(0, 72, 105, 70, 8, 30, "#94a3b8", "Rampa Acceso ADA (Pendiente 1:12)", { x: 0, y: 20, z: 50 });

      return {
        name: "Local Comercial & Coffee Shop (Hormigón ASCE 7)",
        vertices,
        faces,
        dims: { widthMm: 8500, heightMm: 3600, depthMm: 6500 },
        specBadge: "ASCE 7-22 Categoría 5 · 175+ MPH",
        metrics: { weightG: 48500000, material: "Hormigón Armado 3,000 PSI + Acero Grado 60", prStandard: "OGPe / Código de Construcción PR 2018" }
      };
    } else if (modelType === "solar") {
      // Wall Mount Base Frame
      addBox(0, 0, -35, 180, 220, 12, "#1e293b", "Placa Base de Acero Galvanizado", { x: 0, y: 0, z: -35 });
      // Hybrid Inverter Chassis (Top)
      addBox(0, -45, 0, 130, 95, 55, "#0284c7", "Inversor Híbrido 48V 5000W Onda Pura", { x: 0, y: -40, z: 30 });
      // Aluminum Heat Sink Fins (Back of Inverter)
      addBox(0, -45, -22, 120, 85, 12, "#38bdf8", "Disipador Convectivo de Aluminio CNC", { x: 0, y: -40, z: -10 });
      // LiFePO4 48V Battery Rack (Bottom)
      addBox(0, 55, 5, 140, 85, 65, "#0f172a", "Batería LiFePO4 48V 100Ah (5.12 kWh)", { x: 0, y: 40, z: 30 });
      // Heavy DC Breakers & Busbar Enclosure
      addBox(0, 10, 20, 70, 30, 25, "#eab308", "Caja Desconexión DC + Fusible 150A", { x: 0, y: 0, z: 55 });

      return {
        name: "Estación Solar Crítica LiFePO4 48V (Respaldo LUMA)",
        vertices,
        faces,
        dims: { widthMm: 650, heightMm: 950, depthMm: 280 },
        specBadge: "5.12 kWh LiFePO4 · Inversor 5kW",
        metrics: { weightG: 58000, material: "Chasis Acero IP54 + Celdas Litio Grado A", prStandard: "NEC 706 / LUMA Net Metering Compliant" }
      };
    } else if (modelType === "cistern") {
      // Concrete Platform Pad
      addBox(0, 75, 0, 170, 15, 170, "#475569", "Base de Concreto Nivelada 4 Pulgadas", { x: 0, y: 35, z: 0 });
      // Vertical Tank Body (Stacked Hexagonal Segments)
      addBox(0, 0, 0, 120, 130, 120, "#0284c7", "Tanque Polietileno 600 Galones (NSF-61)", { x: 0, y: 0, z: 0 });
      // Top Manhole & Vent Cover
      addBox(0, -70, 0, 55, 14, 55, "#0369a1", "Tapa Roscada con Malla Antimosquitos", { x: 0, y: -45, z: 0 });
      // Booster Water Pump & Press Control (Beside tank)
      addBox(78, 55, 30, 42, 34, 32, "#10b981", "Bomba Periférica 3/4 HP + Press Control", { x: 50, y: 20, z: 30 });
      // IoT Ultrasonic Water Level Sensor Module
      addBox(0, -82, 0, 26, 12, 26, "#a855f7", "Cápsula IoT JSN-SR04T (Nivel en Vivo)", { x: 0, y: -70, z: 0 });
      // PVC Manifold Bypass
      addBox(78, 62, -20, 14, 14, 60, "#e2e8f0", "Tubería PVC Cédula 40 & Válvulas Check", { x: 50, y: 20, z: -20 });

      return {
        name: "Cisterna de Reserva 600 Galones & Bomba IoT",
        vertices,
        faces,
        dims: { widthMm: 1400, heightMm: 1850, depthMm: 1400 },
        specBadge: "600 Galones · Presión Constante 45 PSI",
        metrics: { weightG: 2320000, material: "Polietileno de Alta Densidad Virgen + Bronce", prStandard: "Reglamento Sanitario AAA de Puerto Rico" }
      };
    } else if (modelType === "enclosure") {
      // Lower Waterproof Base Box
      addBox(0, 15, 0, 130, 45, 95, "#0284c7", "Caja Inferior PETG Bambu Lab", { x: 0, y: 30, z: 0 });
      // Neoprene O-Ring Gasket Seal
      addBox(0, -8, 0, 126, 4, 91, "#0f172a", "Junta de Neopreno Hermética IP67", { x: 0, y: 0, z: 0 });
      // Transparent / Solid Lid Top
      addBox(0, -18, 0, 132, 14, 97, "#38bdf8", "Tapa Hermética con 4 Tornillos M3 Inox", { x: 0, y: -45, z: 0 });
      // PG7 Cable Gland Left
      addBox(-72, 15, 0, 18, 18, 18, "#eab308", "Prensaestopas PG7 Hermético", { x: -35, y: 30, z: 0 });
      // Internal PCB Plate (ESP32)
      addBox(0, 18, 0, 95, 6, 65, "#10b981", "PCB ESP32-S3 + Sensores Ambientales", { x: 0, y: 15, z: 0 });

      return {
        name: "Gabinete Hermético IP67 (Bambu Lab PETG)",
        vertices,
        faces,
        dims: { widthMm: 132, heightMm: 62, depthMm: 97 },
        specBadge: "IP67 Sumergible · PETG Resistente al Salitre",
        metrics: { weightG: 220, material: "PETG Bambu Lab + Insertos Latón M3", printHours: 4.8, prStandard: "Resistencia UV y Salitre Marino ASTM B117" }
      };
    } else if (modelType === "workshop") {
      // Heavy Sturdy Work Table Frame
      addBox(0, 25, 0, 220, 14, 120, "#d97706", "Tablero de Madera Tratada Antipolilla", { x: 0, y: 0, z: 0 });
      addBox(-95, 60, -45, 14, 60, 14, "#475569", "Pata Acero Tubular 2 Pulgadas", { x: 0, y: 20, z: 0 });
      addBox(95, 60, -45, 14, 60, 14, "#475569", "Pata Acero Tubular 2 Pulgadas", { x: 0, y: 20, z: 0 });
      addBox(-95, 60, 45, 14, 60, 14, "#475569", "Pata Acero Tubular 2 Pulgadas", { x: 0, y: 20, z: 0 });
      addBox(95, 60, 45, 14, 60, 14, "#475569", "Pata Acero Tubular 2 Pulgadas", { x: 0, y: 20, z: 0 });
      // Bambu Lab 3D Printer Enclosure
      addBox(-50, -20, 0, 75, 75, 75, "#1e293b", "Impresora 3D Bambu Lab X1-C / P1S", { x: -30, y: -30, z: 0 });
      // Soldering & Tool Station
      addBox(55, 10, 10, 40, 20, 30, "#0284c7", "Estación Soldadura T12 + Extractor", { x: 30, y: -10, z: 0 });
      // Rear Pegboard Tool Wall
      addBox(0, -35, -55, 216, 95, 8, "#64748b", "Panel Ranurado Portaherramientas", { x: 0, y: -35, z: -35 });

      return {
        name: "Taller Maker Físico & Banco I+D Prometheus",
        vertices,
        faces,
        dims: { widthMm: 1800, heightMm: 1600, depthMm: 800 },
        specBadge: "Carga 450 kg · Extracción Activa ESD",
        metrics: { weightG: 85000, material: "Madera Pino Tratada 3C + Perfil Acero C", prStandard: "Protección Contra Termitas & Humedad PR" }
      };
    } else if (modelType === "wind") {
      // Concrete Anchor Base Pad
      addBox(0, 85, 0, 90, 15, 90, "#475569", "Zapata de Concreto con Varillas 1/2\"", { x: 0, y: 30, z: 0 });
      // Central Stainless Mast Shaft
      addBox(0, 0, 0, 16, 170, 16, "#cbd5e1", "Eje Central Acero Inox 316", { x: 0, y: 0, z: 0 });
      // Top Bearing Hub
      addBox(0, -85, 0, 45, 14, 45, "#0f172a", "Rodamiento Marino Sellado Cerámico", { x: 0, y: -40, z: 0 });
      // Curved Blade 1 (Left Half)
      addBox(-38, 0, -15, 60, 140, 12, "#0284c7", "Álabe Helicoidal Savonius (PETG UV)", { x: -45, y: 0, z: -20 });
      // Curved Blade 2 (Right Half)
      addBox(38, 0, 15, 60, 140, 12, "#38bdf8", "Álabe Antagónico Eólico", { x: 45, y: 0, z: 20 });
      // Bottom Alternator Dynamo
      addBox(0, 68, 0, 60, 24, 60, "#eab308", "Alternador Trifásico PMG 12V/24V", { x: 0, y: 15, z: 0 });

      return {
        name: "Microgenerador Eólico Savonius Costero",
        vertices,
        faces,
        dims: { widthMm: 450, heightMm: 1200, depthMm: 450 },
        specBadge: "Viento Inicial 3.5 m/s · Autoarranque",
        metrics: { weightG: 9800, material: "PETG con Protección UV + Eje Inox 316", prStandard: "Resistencia a Salitre Costero y Ráfagas Tropicales" }
      };
    } else {
      // Generic Modular Instrument
      addBox(0, 0, 0, 140, 75, 100, isAura ? "#7c3aed" : "#0284c7", "Chasis Principal de Prototipo", { x: 0, y: 0, z: 0 });
      addBox(0, -38, 0, 120, 6, 80, isAura ? "#c084fc" : "#38bdf8", "Cubierta Superior con Difusor LED", { x: 0, y: -30, z: 0 });
      addBox(0, 10, 52, 90, 30, 8, "#0f172a", "Bahía Frontal con Pantalla OLED", { x: 0, y: 0, z: 30 });

      return {
        name: projectTitle || "Prototipo de Ingeniería Física",
        vertices,
        faces,
        dims: { widthMm: 140, heightMm: 75, depthMm: 100 },
        specBadge: "Prototipo Paramétrico Digital",
        metrics: { weightG: 340, material: "PETG / Resina Técnica", printHours: 6.2, prStandard: "Tolerancia ±0.15 mm" }
      };
    }
  }, [modelType, projectTitle, isAura]);

  // Handle Canvas Rendering & Orbit Animation
  useEffect(() => {
    if (!isOpen || activeTab !== "viewport3d") return;

    let animId: number;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let curRotY = cameraRotY;
    let curRotX = cameraRotX;

    const render = () => {
      if (autoRotate && !isDraggingRef.current) {
        curRotY += 0.008;
        setCameraRotY(curRotY);
      } else {
        curRotY = cameraRotY;
        curRotX = cameraRotX;
      }

      const width = canvas.width;
      const height = canvas.height;
      ctx.clearRect(0, 0, width, height);

      // Cybernetic Background Grid & Radial Glow
      const bgGrad = ctx.createRadialGradient(width / 2, height / 2, 20, width / 2, height / 2, width / 1.5);
      if (materialFinish === "hologram" || isAura) {
        bgGrad.addColorStop(0, "rgba(88, 28, 135, 0.25)");
        bgGrad.addColorStop(1, "rgba(5, 5, 10, 0.95)");
      } else {
        bgGrad.addColorStop(0, "rgba(14, 116, 144, 0.2)");
        bgGrad.addColorStop(1, "rgba(6, 9, 15, 0.95)");
      }
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      // Horizon Ground Lines
      ctx.strokeStyle = "rgba(255, 255, 255, 0.05)";
      ctx.lineWidth = 1;
      const groundY = height * 0.78;
      for (let i = -width; i <= width * 2; i += 40) {
        ctx.beginPath();
        ctx.moveTo(i, groundY);
        ctx.lineTo(width / 2 + (i - width / 2) * 2.5, height);
        ctx.stroke();
      }

      // 3D Math Matrix Transforms
      const cosY = Math.cos(curRotY);
      const sinY = Math.sin(curRotY);
      const cosX = Math.cos(curRotX);
      const sinX = Math.sin(curRotX);

      const fov = 460;
      const cameraDistance = 480 - zoomLevel;
      const cx = width / 2;
      const cy = height / 2;

      // Project all vertices with exploded offset if active
      const projectedVerts: { x: number; y: number; z: number; scale: number }[] = [];

      meshData.vertices.forEach((v) => {
        // Find which face uses this vertex to calculate exploded offset
        let offX = 0, offY = 0, offZ = 0;
        if (explodedView) {
          // Find any face containing this vertex index
          const fMatch = meshData.faces.find((f) => f.indices.includes(projectedVerts.length));
          if (fMatch && fMatch.explodeOffset) {
            offX = fMatch.explodeOffset.x;
            offY = fMatch.explodeOffset.y;
            offZ = fMatch.explodeOffset.z;
          }
        }

        const vx = v.x + offX;
        const vy = v.y + offY;
        const vz = v.z + offZ;

        // Rotate Y
        const x1 = vx * cosY - vz * sinY;
        const z1 = vx * sinY + vz * cosY;

        // Rotate X
        const y2 = vy * cosX - z1 * sinX;
        const z2 = vy * sinX + z1 * cosX;

        const depth = cameraDistance + z2;
        const scale = fov / Math.max(depth, 50);
        const px = cx + x1 * scale;
        const py = cy + y2 * scale;

        projectedVerts.push({ x: px, y: py, z: z2, scale });
      });

      // Directional Light Vector (from upper-front-right)
      const lx = 0.55;
      const ly = -0.75;
      const lz = -0.35;
      const lLen = Math.sqrt(lx * lx + ly * ly + lz * lz);
      const nlx = lx / lLen;
      const nly = ly / lLen;
      const nlz = lz / lLen;

      // Sort faces by depth for Painter's algorithm
      const sortedFaces = meshData.faces.map((face) => {
        const v0 = projectedVerts[face.indices[0]];
        const v1 = projectedVerts[face.indices[1]];
        const v2 = projectedVerts[face.indices[2]];
        const avgZ = (v0.z + v1.z + v2.z) / 3;
        return { face, avgZ, v0, v1, v2 };
      });

      sortedFaces.sort((a, b) => b.avgZ - a.avgZ);

      // Draw each face
      sortedFaces.forEach(({ face, v0, v1, v2 }) => {
        // Backface Culling via 2D cross product of screen coordinates
        const cross = (v1.x - v0.x) * (v2.y - v0.y) - (v1.y - v0.y) * (v2.x - v0.x);

        if (cross > 0 || viewWireframe) {
          // Normal calculation
          const p0 = meshData.vertices[face.indices[0]];
          const p1 = meshData.vertices[face.indices[1]];
          const p2 = meshData.vertices[face.indices[2]];

          const ax = p1.x - p0.x, ay = p1.y - p0.y, az = p1.z - p0.z;
          const bx = p2.x - p0.x, by = p2.y - p0.y, bz = p2.z - p0.z;

          const nx = ay * bz - az * by;
          const ny = az * bx - ax * bz;
          const nz = ax * by - ay * bx;
          const nLen = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;

          const dot = Math.max(0.18, (nx / nLen) * nlx + (ny / nLen) * nly + (nz / nLen) * nlz);

          ctx.beginPath();
          ctx.moveTo(v0.x, v0.y);
          ctx.lineTo(v1.x, v1.y);
          ctx.lineTo(v2.x, v2.y);
          ctx.closePath();

          if (viewWireframe) {
            ctx.strokeStyle = materialFinish === "hologram" || isAura ? "rgba(192, 132, 252, 0.85)" : "rgba(56, 189, 248, 0.85)";
            ctx.lineWidth = 1;
            ctx.stroke();
          } else {
            // Material colors
            if (materialFinish === "hologram") {
              ctx.fillStyle = `rgba(168, 85, 247, ${0.12 + dot * 0.35})`;
              ctx.strokeStyle = "rgba(192, 132, 252, 0.7)";
              ctx.lineWidth = 1;
              ctx.fill();
              ctx.stroke();
            } else if (materialFinish === "concrete") {
              const lum = Math.round(70 + dot * 90);
              ctx.fillStyle = `rgb(${lum}, ${lum + 4}, ${lum + 8})`;
              ctx.strokeStyle = "rgba(0, 0, 0, 0.4)";
              ctx.lineWidth = 0.8;
              ctx.fill();
              ctx.stroke();
            } else if (materialFinish === "aluminum") {
              const lum = Math.round(110 + dot * 130);
              ctx.fillStyle = `rgb(${lum}, ${lum + 10}, ${lum + 20})`;
              ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
              ctx.lineWidth = 0.8;
              ctx.fill();
              ctx.stroke();
            } else {
              // PETG / Default Custom Palette
              const baseHex = face.color || (isAura ? "#9333ea" : "#0284c7");
              // Parse hex to rgb
              const r = parseInt(baseHex.slice(1, 3), 16) || 2;
              const g = parseInt(baseHex.slice(3, 5), 16) || 132;
              const b = parseInt(baseHex.slice(5, 7), 16) || 199;

              const rShade = Math.min(255, Math.round(r * (0.35 + dot * 0.75)));
              const gShade = Math.min(255, Math.round(g * (0.35 + dot * 0.75)));
              const bShade = Math.min(255, Math.round(b * (0.35 + dot * 0.75)));

              ctx.fillStyle = `rgb(${rShade}, ${gShade}, ${bShade})`;
              ctx.strokeStyle = "rgba(0, 0, 0, 0.25)";
              ctx.lineWidth = 0.6;
              ctx.fill();
              ctx.stroke();
            }
          }
        }
      });

      animId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animId);
    };
  }, [isOpen, activeTab, autoRotate, cameraRotX, cameraRotY, zoomLevel, viewWireframe, explodedView, materialFinish, meshData, isAura]);

  // Touch / Mouse controls for orbit rotation
  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    isDraggingRef.current = true;
    lastMousePos.current = { x: e.clientX, y: e.clientY };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDraggingRef.current) return;
    const deltaX = e.clientX - lastMousePos.current.x;
    const deltaY = e.clientY - lastMousePos.current.y;
    lastMousePos.current = { x: e.clientX, y: e.clientY };

    setCameraRotY((prev) => prev + deltaX * 0.01);
    setCameraRotX((prev) => Math.max(-1.4, Math.min(1.4, prev + deltaY * 0.01)));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    isDraggingRef.current = false;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}
  };

  const handleWheel = (e: React.WheelEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setZoomLevel((prev) => Math.max(-200, Math.min(200, prev - e.deltaY * 0.4)));
  };

  // Generate ASCII STL download
  const handleExportSTL = () => {
    let stl = `solid ${meshData.name.replace(/[^a-zA-Z0-9]/g, "_")}\n`;
    meshData.faces.forEach((f) => {
      const v0 = meshData.vertices[f.indices[0]];
      const v1 = meshData.vertices[f.indices[1]];
      const v2 = meshData.vertices[f.indices[2]];
      stl += `  facet normal 0 0 1\n    outer loop\n`;
      stl += `      vertex ${v0.x.toFixed(3)} ${v0.y.toFixed(3)} ${v0.z.toFixed(3)}\n`;
      stl += `      vertex ${v1.x.toFixed(3)} ${v1.y.toFixed(3)} ${v1.z.toFixed(3)}\n`;
      stl += `      vertex ${v2.x.toFixed(3)} ${v2.y.toFixed(3)} ${v2.z.toFixed(3)}\n`;
      stl += `    endloop\n  endfacet\n`;
    });
    stl += `endsolid ${meshData.name.replace(/[^a-zA-Z0-9]/g, "_")}\n`;

    const blob = new Blob([stl], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${meshData.name.toLowerCase().replace(/[^a-z0-9]/g, "_")}_prototype.stl`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("✓ Archivo 3D STL descargado para Bambu Studio / FreeCAD.");
  };

  // Send to Blender (Port 8081) or FreeCAD (Port 8082) Bridge
  const handleSendToCADBridge = async (target: "blender" | "freecad") => {
    const port = target === "blender" ? 8081 : 8082;
    toast.info(`📐 Conectando con ${target.toUpperCase()} en puerto localhost:${port}...`);
    try {
      const res = await fetch(`http://localhost:${port}/api/inject`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: meshData.name,
          dims: meshData.dims,
          verticesCount: meshData.vertices.length,
          facesCount: meshData.faces.length
        })
      });
      if (res.ok) {
        toast.success(`✓ Prototipo inyectado exitosamente en ${target.toUpperCase()}!`);
      } else {
        throw new Error("Bridge offline");
      }
    } catch {
      toast.warning(`⚠️ Servidor ${target.toUpperCase()} en puerto ${port} no detectado. El script y STL están listos para importar manualmente.`);
    }
  };

  // Cinematic prompt generator for AI video
  const generatedPrompt = `Cinematic 3D photorealistic demonstration video of "${projectTitle}", physical engineering prototype.
Dimensions: ${meshData.dims.widthMm}x${meshData.dims.heightMm}x${meshData.dims.depthMm}mm.
Finish: ${materialFinish === "petg" ? "Matte Caribbean UV-resistant PETG casing" : materialFinish === "concrete" ? "Reinforced ASCE 7 hurricane concrete structure" : "Anodized brushed aerospace aluminum"}.
Camera motion: ${cameraMotion}. Lighting setup: ${lightingStyle}. Resolution: 8K Octane render 60fps.`;

  const copyPrompt = () => {
    navigator.clipboard.writeText(generatedPrompt);
    setCopiedPrompt(true);
    toast.success("✓ Prompt cinemático copiado para Sora / Higgsfield / Seedance.");
    setTimeout(() => setCopiedPrompt(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-void/85 backdrop-blur-md font-mono-tech animate-in fade-in duration-200">
      <div className="w-full max-w-4xl bg-card border border-primary/40 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">
        
        {/* Header Bar */}
        <div className="p-3.5 sm:p-4 bg-void/90 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`p-2 rounded-xl border flex items-center justify-center shrink-0 ${
              isAura ? "bg-purple-500/10 border-purple-500/30 text-purple-400" : "bg-primary/10 border-primary/30 text-primary"
            }`}>
              <Box className="w-5 h-5 animate-pulse" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-serif-display text-sm sm:text-base font-bold text-foreground truncate">
                  Prototipo 3D Interactivo: {meshData.name}
                </h3>
                <span className="hidden sm:inline text-[9px] px-2 py-0.5 rounded font-bold bg-primary/15 text-primary border border-primary/30">
                  {meshData.specBadge}
                </span>
              </div>
              <p className="text-[10px] text-muted-foreground font-sans truncate">
                {projectDescription || "Visualización paramétrica volumétrica y telemetría de fabricación"}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5">
            {/* 3 View Tabs: Finished Render, 3D Blockout, and Cinematic Prompt */}
            <div className="flex items-center bg-card border border-border p-0.5 rounded-lg text-xs">
              <button
                type="button"
                onClick={() => setActiveTab("finishedRender")}
                className={`px-2.5 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 font-bold ${
                  activeTab === "finishedRender" ? "bg-emerald-600 text-white shadow-xs" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Render Terminado</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("viewport3d")}
                className={`px-2.5 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 font-bold ${
                  activeTab === "viewport3d" ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Box className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Croquis 3D</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("cinematicPrompt")}
                className={`px-2.5 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 font-bold ${
                  activeTab === "cinematicPrompt" ? "bg-purple-600 text-white shadow-xs" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Film className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Video AI</span>
              </button>
            </div>

            <button
              onClick={onClose}
              className="p-1.5 text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted cursor-pointer transition-colors"
              title="Cerrar estudio 3D"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Studio Content */}
        <div className="p-3 sm:p-4 overflow-y-auto flex-1 space-y-3">
          {activeTab === "finishedRender" ? (
            /* TAB 1: PHOTOREALISTIC FINISHED PROJECT RENDER (ALMOST FINISHED / FINAL STATE) */
            <div className="space-y-4 animate-in fade-in duration-200">
              {/* Main High-Resolution Finished Image Container */}
              <div className="relative rounded-2xl overflow-hidden border border-emerald-500/40 bg-black group shadow-xl">
                <img
                  src={finishedRenderImage}
                  alt={meshData.name}
                  onClick={() => setIsPhotoZoomed(true)}
                  className="w-full max-h-[440px] sm:max-h-[500px] object-cover sm:object-contain bg-black/90 cursor-zoom-in group-hover:scale-101 transition-transform duration-300"
                />

                {/* Overlaid Badges */}
                <div className="absolute top-3 left-3 flex items-center gap-2 flex-wrap">
                  <span className="px-3 py-1 rounded-lg text-[10px] font-bold bg-emerald-600/90 text-white shadow-lg backdrop-blur-md flex items-center gap-1.5 border border-emerald-400/30">
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>Proyecto Casi Terminado (Visión Final As-Built)</span>
                  </span>
                  <span className="px-2.5 py-1 rounded-lg text-[9.5px] font-bold bg-black/75 text-white shadow backdrop-blur-md border border-white/20">
                    🇵🇷 Contexto Puerto Rico
                  </span>
                </div>

                <div className="absolute top-3 right-3 flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setIsPhotoZoomed(true)}
                    className="p-2 rounded-xl bg-card/85 backdrop-blur-md border border-border text-foreground hover:bg-muted cursor-pointer shadow-lg transition-all"
                    title="Ver en pantalla completa"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                  <a
                    href={finishedRenderImage}
                    download={`${meshData.name.toLowerCase().replace(/[^a-z0-9]/g, "_")}_render_terminado.jpg`}
                    className="p-2 rounded-xl bg-card/85 backdrop-blur-md border border-border text-foreground hover:bg-muted cursor-pointer shadow-lg transition-all flex items-center justify-center"
                    title="Descargar Render en Alta Definición"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                </div>

                <div className="absolute bottom-3 left-3 right-3 p-3 bg-card/85 backdrop-blur-md border border-border/80 rounded-xl shadow-lg flex items-center justify-between flex-wrap gap-2 text-xs">
                  <div className="min-w-0">
                    <h4 className="font-bold text-foreground truncate">{meshData.name}</h4>
                    <p className="text-[10px] text-muted-foreground font-sans truncate">
                      {finishedProjectDetails.subtitle}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab("viewport3d")}
                    className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground font-bold text-xs flex items-center gap-1.5 hover:opacity-90 transition-all cursor-pointer shrink-0 shadow-xs"
                  >
                    <Box className="w-3.5 h-3.5" />
                    <span>Ver Croquis 3D Dimensional</span>
                  </button>
                </div>
              </div>

              {/* As-Built Engineering Metrics Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {finishedProjectDetails.asBuiltSpecs.map((sp, idx) => (
                  <div key={idx} className="p-3 rounded-xl bg-card border border-border space-y-0.5 shadow-2xs">
                    <span className="text-[9px] uppercase text-muted-foreground font-bold block">{sp.label}</span>
                    <span className="font-bold text-foreground block truncate">{sp.val}</span>
                  </div>
                ))}
              </div>

              {/* Key Handover & Architectural Features */}
              <div className="p-4 rounded-xl bg-card/60 border border-border space-y-2.5">
                <h5 className="font-serif-display text-xs font-bold text-foreground flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-500 stroke-[3]" />
                  Especificaciones del Estado Final de Obra & Materiales
                </h5>
                <div className="grid sm:grid-cols-2 gap-2 text-xs text-muted-foreground font-sans">
                  {finishedProjectDetails.features.map((feat, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-void/50 border border-border/60">
                      <span className="text-emerald-500 font-bold shrink-0 mt-0.5">✓</span>
                      <span className="text-[11px] leading-relaxed text-foreground/90">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : activeTab === "viewport3d" ? (
            <div className="space-y-3">
              
              {/* 3D Canvas Viewport Container */}
              <div className="relative w-full h-[320px] sm:h-[420px] rounded-2xl overflow-hidden border border-primary/30 bg-black shadow-inner select-none cursor-grab active:cursor-grabbing">
                <canvas
                  ref={canvasRef}
                  width={800}
                  height={500}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onWheel={handleWheel}
                  className="w-full h-full object-cover touch-none"
                />

                {/* Overlaid 3D Viewport Controls (Top Right) */}
                <div className="absolute top-3 right-3 flex flex-wrap items-center gap-1.5 bg-card/85 backdrop-blur-md border border-border/80 p-1.5 rounded-xl shadow-lg">
                  <button
                    type="button"
                    onClick={() => setAutoRotate(!autoRotate)}
                    className={`px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all cursor-pointer ${
                      autoRotate ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                    title="Alternar rotación continua automática"
                  >
                    <RotateCw className={`w-3 h-3 ${autoRotate ? "animate-spin" : ""}`} />
                    <span className="hidden md:inline">Girar</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setExplodedView(!explodedView)}
                    className={`px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all cursor-pointer ${
                      explodedView ? "bg-amber-500 text-black shadow-xs" : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                    title="Vista explotada: separar componentes mecánicos"
                  >
                    <Layers className="w-3 h-3" />
                    <span>Despiece</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setViewWireframe(!viewWireframe)}
                    className={`px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all cursor-pointer ${
                      viewWireframe ? "bg-sky-500 text-white" : "bg-muted text-muted-foreground hover:text-foreground"
                    }`}
                    title="Alternar vista de malla alámbrica"
                  >
                    <Eye className="w-3 h-3" />
                    <span className="hidden md:inline">Malla</span>
                  </button>

                  <div className="flex items-center gap-0.5 border-l border-border/60 pl-1">
                    <button
                      type="button"
                      onClick={() => setZoomLevel((z) => Math.min(200, z + 35))}
                      className="w-6 h-6 rounded bg-muted hover:bg-muted/80 text-foreground font-bold flex items-center justify-center text-xs cursor-pointer"
                      title="Acercar zoom"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      onClick={() => setZoomLevel((z) => Math.max(-200, z - 35))}
                      className="w-6 h-6 rounded bg-muted hover:bg-muted/80 text-foreground font-bold flex items-center justify-center text-xs cursor-pointer"
                      title="Alejar zoom"
                    >
                      -
                    </button>
                  </div>
                </div>

                {/* Overlaid Material Finish Switcher (Bottom Left) */}
                <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-card/85 backdrop-blur-md border border-border/80 p-1 rounded-xl shadow-lg text-[9.5px]">
                  <span className="text-muted-foreground font-bold px-1 hidden sm:inline">Material:</span>
                  {[
                    { id: "petg", label: "PETG / Plástico" },
                    { id: "concrete", label: "Hormigón" },
                    { id: "aluminum", label: "Aluminio" },
                    { id: "hologram", label: "Holograma" }
                  ].map((mat) => (
                    <button
                      key={mat.id}
                      type="button"
                      onClick={() => setMaterialFinish(mat.id as any)}
                      className={`px-2 py-0.8 rounded-lg font-bold transition-all cursor-pointer ${
                        materialFinish === mat.id ? "bg-primary text-primary-foreground shadow-2xs" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {mat.label}
                    </button>
                  ))}
                </div>

                {/* Overlaid Interaction Helper */}
                <div className="absolute bottom-3 right-3 text-[9px] text-muted-foreground/80 pointer-events-none hidden sm:block">
                  Arrastra con mouse o toca para orbitar 360°
                </div>
              </div>

              {/* Physical Specifications & Telemetry Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="p-2.5 rounded-xl bg-card border border-border space-y-0.5">
                  <span className="text-[9px] uppercase text-muted-foreground font-bold block">Dimensiones (X x Y x Z)</span>
                  <span className="font-bold text-foreground">
                    {meshData.dims.widthMm} x {meshData.dims.heightMm} x {meshData.dims.depthMm} mm
                  </span>
                </div>
                <div className="p-2.5 rounded-xl bg-card border border-border space-y-0.5">
                  <span className="text-[9px] uppercase text-muted-foreground font-bold block">Masa / Peso Estimado</span>
                  <span className="font-bold text-foreground">
                    {meshData.metrics.weightG > 1000 ? `${(meshData.metrics.weightG / 1000).toFixed(1)} kg` : `${meshData.metrics.weightG} g`}
                  </span>
                </div>
                <div className="p-2.5 rounded-xl bg-card border border-border space-y-0.5">
                  <span className="text-[9px] uppercase text-muted-foreground font-bold block">Material Predominante</span>
                  <span className="font-bold text-foreground truncate block">
                    {meshData.metrics.material}
                  </span>
                </div>
                <div className="p-2.5 rounded-xl bg-card border border-border space-y-0.5">
                  <span className="text-[9px] uppercase text-muted-foreground font-bold block">Normativa Puerto Rico</span>
                  <span className="font-bold text-primary truncate block">
                    {meshData.metrics.prStandard || "ASCE 7 / Clima Tropical"}
                  </span>
                </div>
              </div>

              {/* Action Toolbar: STL, Blender & FreeCAD */}
              <div className="p-3 rounded-xl bg-card/60 border border-border flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleExportSTL}
                    className="px-3 py-1.5 bg-primary text-primary-foreground font-bold text-xs rounded-lg shadow-sm hover:opacity-90 flex items-center gap-1.5 transition-all cursor-pointer"
                  >
                    <Download className="w-3.5 h-3.5" />
                    <span>Descargar Modelo STL (3D)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSendToCADBridge("freecad")}
                    className="px-3 py-1.5 bg-card border border-border hover:bg-muted text-foreground font-bold text-xs rounded-lg flex items-center gap-1.5 transition-all cursor-pointer"
                    title="Inyectar en FreeCAD vía localhost:8082"
                  >
                    <Terminal className="w-3.5 h-3.5 text-sky-400" />
                    <span className="hidden sm:inline">FreeCAD (8082)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleSendToCADBridge("blender")}
                    className="px-3 py-1.5 bg-card border border-border hover:bg-muted text-foreground font-bold text-xs rounded-lg flex items-center gap-1.5 transition-all cursor-pointer"
                    title="Inyectar en Blender vía localhost:8081"
                  >
                    <Film className="w-3.5 h-3.5 text-amber-400" />
                    <span className="hidden sm:inline">Blender (8081)</span>
                  </button>
                </div>

                <span className="text-[9px] text-muted-foreground">
                  Geometría estanca · Malla STL compatible con Bambu Studio, OrcaSlicer y CNC
                </span>
              </div>
            </div>
          ) : (
            /* Cinematic Prompt Generator Tab (for Sora / Higgsfield) */
            <div className="space-y-4 animate-in fade-in duration-150">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <label className="text-[10px] text-primary uppercase font-bold flex items-center gap-1">
                    <Camera className="w-3.5 h-3.5" /> Movimiento Cámara:
                  </label>
                  <select
                    value={cameraMotion}
                    onChange={(e) => setCameraMotion(e.target.value as any)}
                    className="w-full bg-void border border-border px-3 py-1.5 rounded-lg text-xs font-mono text-foreground focus:outline-none"
                  >
                    <option value="orbit360">Giro Orbital 360°</option>
                    <option value="macroHero">Macro Primer Plano</option>
                    <option value="explosiveAssembly">Despiece / Ensamble Mecánico</option>
                    <option value="fpvDrone">Barrido Dinámico FPV</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] text-primary uppercase font-bold flex items-center gap-1">
                    <SunMedium className="w-3.5 h-3.5" /> Iluminación & Set:
                  </label>
                  <select
                    value={lightingStyle}
                    onChange={(e) => setLightingStyle(e.target.value as any)}
                    className="w-full bg-void border border-border px-3 py-1.5 rounded-lg text-xs font-mono text-foreground focus:outline-none"
                  >
                    <option value="cyberNeon">Ciber Neón / Violeta & Cian</option>
                    <option value="industrialStudio">Estudio Industrial 5000K</option>
                    <option value="sunsetTropical">Atardecer Tropical PR</option>
                    <option value="cleanMinimal">Minimalista Blanco / Apple</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] text-primary uppercase font-bold flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> Calidad de Render:
                  </label>
                  <select
                    value={renderQuality}
                    onChange={(e) => setRenderQuality(e.target.value as any)}
                    className="w-full bg-void border border-border px-3 py-1.5 rounded-lg text-xs font-mono text-foreground focus:outline-none"
                  >
                    <option value="8kPhotoreal">8K Fotorrealista (Octane)</option>
                    <option value="cinematicFilm">Cine 35mm Anamórfico</option>
                    <option value="blueprintHologram">Holograma & Wireframe</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2 pt-2 border-t border-border/40">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase font-bold text-muted-foreground font-mono">
                    Prompt Maestro Optimizado para Video Generativo (Sora / Higgsfield / Seedance):
                  </span>
                  <span className="text-[9px] text-purple-400 font-bold px-2 py-0.5 rounded bg-purple-500/10 border border-purple-500/20">
                    {isAura ? "🌌 Aura OS Profile" : "🔥 Prometheus Profile"}
                  </span>
                </div>

                <textarea
                  readOnly
                  value={generatedPrompt}
                  rows={5}
                  className="w-full p-3 rounded-xl bg-void border border-border font-mono text-xs text-emerald-400 focus:outline-none select-all"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={copyPrompt}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-lg cursor-pointer transition-all flex items-center gap-1.5 shadow-md"
                >
                  {copiedPrompt ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  <span>{copiedPrompt ? "¡Prompt Copiado!" : "Copiar Prompt para Video"}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Full Size Image Viewer for Finished Render */}
      {isPhotoZoomed && (
        <div
          onClick={() => setIsPhotoZoomed(false)}
          className="fixed inset-0 z-60 flex items-center justify-center p-3 sm:p-6 bg-black/95 backdrop-blur-lg cursor-pointer animate-in fade-in duration-200"
        >
          <div className="relative max-w-5xl max-h-[92vh] flex flex-col items-center">
            <img
              src={finishedRenderImage}
              alt={meshData.name}
              className="max-w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl border border-emerald-500/40"
            />
            <div className="mt-3 flex items-center gap-3">
              <span className="text-xs font-mono-tech text-emerald-400 font-bold bg-black/70 px-3 py-1 rounded-full border border-emerald-500/30">
                {meshData.name} — Render de Proyecto Terminado
              </span>
              <button
                type="button"
                onClick={() => setIsPhotoZoomed(false)}
                className="px-3 py-1 rounded-full bg-card text-foreground border border-border text-xs font-bold hover:bg-muted cursor-pointer"
              >
                Cerrar (ESC)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
